import { Modal, ModalContent } from '@finn/design-system/atoms/modal';
import { Image } from '@finn/ui-components';
import { getCloudinaryImgUrl } from '@finn/ui-utils';
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useState,
} from 'react';

import { VehicleContext } from '../../product-cards';

export type EmissionLabelData = {
  title: string;
  co2emission: {
    label: string;
    unit: string;
  };
  consumption: {
    label: string;
    consumption_unit: string;
    consumption_city_unit: string;
    consumption_highway_unit: string;
  };
  disclaimer: string;
};

// TODO in order to avoid cycle dependencies
// we are accessing nativeAppSDK directly instead of getAppSDK method
// in 3-6 months, after native app fully migrated to @finn/ua-app sdk
// this code can be deleted DRI - @andrii.tiertyshnyi
const getAppSDK = () => {
  type CustomWindow = {
    nativeAppSDK: {
      sendMessageToApp: (props: {
        type: 'event:open_modal' | 'event:close_modal';
        value?: string;
      }) => void;
    };
  };

  const globalObject = window as unknown as CustomWindow;

  const nativeAppSDK = globalObject?.nativeAppSDK;

  return nativeAppSDK;
};

type EmissionModalProviderProps = {
  emissionLabel: EmissionLabelData;
  children?: ReactNode;
};

type EmissionModalContextProps = {
  emissionLabel: EmissionLabelData;
  modalOpen: boolean;
  setModalOpen: Dispatch<SetStateAction<boolean>>;
};

export const EmissionModalContext = createContext<EmissionModalContextProps>(
  {} as EmissionModalContextProps
);

export const EmissionModalProvider = ({
  children,
  emissionLabel,
}: EmissionModalProviderProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleGalleryFullScreenChange = useCallback((value: boolean) => {
    setModalOpen(value);

    getAppSDK()?.sendMessageToApp?.({
      type: `event:${value ? 'open' : 'close'}_modal`,
      value: 'emissions_dialog',
    });
  }, []);

  return (
    <EmissionModalContext.Provider
      value={{
        emissionLabel,
        modalOpen,
        setModalOpen: (value) => handleGalleryFullScreenChange(Boolean(value)),
      }}
    >
      {children}
    </EmissionModalContext.Provider>
  );
};

type EmissionModalProps = {
  open: boolean;
  onClose: () => void;
};

export const EmissionModal = ({ open, onClose }: EmissionModalProps) => {
  const { vehicle } = useContext(VehicleContext);
  const efficiencyImage = vehicle?.energy_class_file?.url ?? '';

  return (
    <Modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <ModalContent>
        <Image
          variant="lazy-loading"
          alt="Efficiency Class"
          className="mt-2 flex w-full flex-col"
          src={getCloudinaryImgUrl(efficiencyImage)}
        />
      </ModalContent>
    </Modal>
  );
};
